import { Navigate } from 'react-router-dom';
import { usePlugins } from '@/contexts/PluginContext';
import { DEFAULT_AUTHENTICATED_ROUTE } from '@/config/routes';

interface ProtectedPluginRouteProps {
  pluginType: string;
  children: React.ReactNode;
}

/**
 * ProtectedPluginRoute Component
 * Wraps plugin-specific routes and redirects if plugin is disabled
 *
 * @example
 * <ProtectedPluginRoute pluginType="BOOKING">
 *   <BookingPage />
 * </ProtectedPluginRoute>
 */
export function ProtectedPluginRoute({ pluginType, children }: ProtectedPluginRouteProps) {
  const { isPluginEnabled, loading } = usePlugins();

  // Show nothing while loading plugin state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2"
          style={{ borderColor: 'var(--theme-accent)' }}
        />
      </div>
    );
  }

  // Redirect to my-bookings if plugin is disabled
  if (!isPluginEnabled(pluginType)) {
    return <Navigate to={DEFAULT_AUTHENTICATED_ROUTE} replace />;
  }

  // Render children if plugin is enabled
  return <>{children}</>;
}
